import { IsLoading } from "src/common/components/AppState/IsLoading";
import {
  addMedia,
  checkMediaExists,
  fetchFilteredMedias,
  getElevenlabsVoices,
  getPreferredElevenLabsVoice,
  resetTtsPreview,
  duplicateAvatar,
} from "../state/gallerySlice";
import { useTranslation } from "react-i18next";
import { MediaNameInput } from "src/common/components/Input/MediaNameInput";
import { useEffect, useMemo, useState } from "react";
import { loadingSelector, LoadingState } from "src/common/state/slice/appStateSlice";
import type { RootState } from "src/common/state/store";
import { GENERATE_MEDIA_PANEL_COMPONENT } from "./GenerateMediaPanel";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { isStringNullOrEmpty } from "src/common/util/isStringNullOrEmpty";
import type { IMedia } from "src/model/unityObject";
import { trackEvent } from "src/trackEvent";
import { useEnsureElevenlabsVoices } from "src/features/gallery/useEnsureElevenlabsVoices";
import { VoicePicker } from "./VoicePicker";
import type { IElevenlabsVoice } from "../state/gallerySlice";

export const CreateAvatar = ({
  isOpen,
  onClose,
  onMediaCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onMediaCreated: (media: IMedia) => void;
}) => {
  useEnsureElevenlabsVoices();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const elevenLabsVoices = useAppSelector(getElevenlabsVoices);

  const { loading } = useAppSelector((state: RootState) => {
    return {
      loading: loadingSelector(state, GENERATE_MEDIA_PANEL_COMPONENT),
    };
  });

  const [mediaNameToEdit, setMediaNameToEdit] = useState<string>("");
  const [tagEditorKey, setTagEditorKey] = useState(0);
  const defaultTtsVoice = useAppSelector(getPreferredElevenLabsVoice);
  const [availableAvatars, setAvailableAvatars] = useState<IMedia[]>([]);

  useEffect(() => {
    let cancelled = false;
    dispatch(
      fetchFilteredMedias({
        mediaType: "avatar",
        limit: 200,
        owner: "public",
        componentId: GENERATE_MEDIA_PANEL_COMPONENT,
        skipStoreUpdate: true,
      }),
    )
      .unwrap()
      .then((res) => {
        if (!cancelled) setAvailableAvatars(res.data.results ?? []);
      })
      .catch(() => {
        if (!cancelled) setAvailableAvatars([]);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  type VoiceSelection = {
    voiceId: string;
    ttsProvider: string;
    name?: string;
    gender?: string;
  };

  const [voiceFr, setVoiceFr] = useState<VoiceSelection>(defaultTtsVoice);
  const [voiceEn, setVoiceEn] = useState<VoiceSelection>(defaultTtsVoice);
  const [voiceEs, setVoiceEs] = useState<VoiceSelection>(defaultTtsVoice);
  const [isSaving, setIsSaving] = useState(false);
  const [nameUnique, setNameUnique] = useState(true);
  const [selectedAvatar, setSelectedAvatar] = useState<IMedia | null>(null);

  const toVoiceSelection = (voice: IElevenlabsVoice): VoiceSelection => ({
    voiceId: voice.id,
    ttsProvider: voice.tts_provider,
    name: voice.name,
    gender: voice.gender,
  });

  const voicePickers = useMemo(() => {
    const pickerFor = (
      label: string,
      lang: string,
      selected: VoiceSelection,
      setSelected: (selection: VoiceSelection) => void,
    ) => {
      const voices = elevenLabsVoices.filter(
        (voice) => voice.language === lang || voice.tts_provider === "elevenLabs",
      );
      const current = voices.find((voice) => voice.id === selected.voiceId) ?? voices[0];
      return {
        label,
        lang,
        voices,
        setSelected,
        selection: current ? toVoiceSelection(current) : selected,
      };
    };
    return [
      pickerFor("FR", "fr", voiceFr, setVoiceFr),
      pickerFor("EN", "en", voiceEn, setVoiceEn),
      pickerFor("ES", "es", voiceEs, setVoiceEs),
    ];
  }, [elevenLabsVoices, voiceFr, voiceEn, voiceEs]);

  const resetAvatar = () => {
    setMediaNameToEdit("");
    dispatch(resetTtsPreview());
    setTagEditorKey(tagEditorKey + 1);

    setVoiceFr(defaultTtsVoice);
    setVoiceEn(defaultTtsVoice);
    setVoiceEs(defaultTtsVoice);

    setSelectedAvatar(null);
  };

  useEffect(() => {
    resetAvatar();
  }, [isOpen]);

  const saveAvatar = async () => {
    if (!selectedAvatar || isSaving) return;

    const nameCheck = await dispatch(checkMediaExists({ name: mediaNameToEdit })).unwrap();

    if (nameCheck.exists) {
      setNameUnique(false);
      return;
    } else {
      setNameUnique(true);
    }

    trackEvent("avatar-create");
    setIsSaving(true);
    try {
      const media = await dispatch(
        duplicateAvatar({
          avatarId: selectedAvatar.id,
          name: mediaNameToEdit,
          voices: voicePickers.map(({ lang, selection }) => ({
            voiceId: selection.voiceId,
            lang,
            gender: selection.gender || "unknown",
            prettyVoiceName: selection.name || "unknown",
            tts_provider: selection.ttsProvider,
          })),
          componentId: GENERATE_MEDIA_PANEL_COMPONENT,
        }),
      ).unwrap();

      dispatch(addMedia(media));
      onClose();
      onMediaCreated(media);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex-1 h-full min-h-0 w-full overflow-x-hidden flex flex-col">
      <IsLoading
        componentId={GENERATE_MEDIA_PANEL_COMPONENT}
        spinnerPlaceholder
        showSuccess={false}
        spinnerStyle={{ marginTop: "4vh" }}
      >
        <div className="flex-1 min-h-0 w-full overflow-y-scroll overflow-x-hidden rounded-lg bg-gray-100">
          {availableAvatars.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              {t("pages.gallery.noAvatarsAvailable") || "No avatars available"}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 p-4">
              {availableAvatars.map((avatar: IMedia) => (
                <div
                  key={avatar.id}
                  className={`cursor-pointer rounded-xl p-2 transition-all bg-white shadow-md hover:scale-105 hover:shadow-2xl hover:ring-green-500 hover:ring-4 ${
                    selectedAvatar?.id === avatar.id ? "ring-purple-500 ring-4 scale-105" : ""
                  }`}
                  onClick={() => setSelectedAvatar(avatar)}
                >
                  <img
                    src={avatar.thumbnail_s3_url || avatar.s3_url}
                    alt={avatar.name}
                    className="w-full h-32 object-cover rounded"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 mt-4">
          <div>
            <div>
              <div className="block font-medium text-gray-700 ">{t("general.fileName")}</div>
              <MediaNameInput
                value={mediaNameToEdit}
                onChange={setMediaNameToEdit}
                maxLength={55}
                disabled={loading === LoadingState.Loading}
              />
              <div className={`text-red-600 ${nameUnique ? "invisible" : ""}`}>
                {t("general.mediaNameAlreadyUsed")}
              </div>
            </div>

            {voicePickers.map(({ label, selection, setSelected, voices }) => (
              <VoicePicker
                key={label}
                label={`${t("pages.gallery.generateVoicePanel.voice")} ${label}`}
                voices={voices}
                selectedVoiceId={selection.voiceId}
                onSelect={(voice) => setSelected(toVoiceSelection(voice))}
                placement="top"
              />
            ))}
            <div className="mt-2">
              <button
                disabled={
                  !selectedAvatar ||
                  isStringNullOrEmpty(mediaNameToEdit) ||
                  mediaNameToEdit.length > 55 ||
                  mediaNameToEdit.trim() === "" ||
                  isSaving
                }
                className={"btn-primary-fill mr-3 mt-2 disabled:cursor-not-allowed"}
                onClick={saveAvatar}
              >
                {t("general.save")}
              </button>
            </div>
          </div>
        </div>
      </IsLoading>
    </div>
  );
};
